import { Entity } from './Entity';
import { SETTINGS, COLORS } from '../config/settings';

export class Player implements Entity {
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public active: boolean = true;

  private speed: number;
  private hp: number;
  private maxHp: number;
  private lives: number;
  private invulnerable: boolean = false;
  private invulnTimer: number = 0;
  
  // Power-up states with durations (milliseconds)
  public shieldMS: number = 0;
  public rapidMS: number = 0;
  public scoreMS: number = 0;
  public bassMS: number = 0;
  public tempoMS: number = 0;
  public doubleMS: number = 0;
  public tripleMS: number = 0;
  public freezeMS: number = 0;
  public magnetMS: number = 0;
  public weaponMS: number = 0;
  
  // Power-up effects
  private fireRate: number;
  private hasWideBeam: boolean = false;
  private hasPiercing: boolean = false;
  private shieldHits: number = 0;
  private scoreMultiplier: number = 1;
  private autoCollect: boolean = false;
  
  // Weapon state
  private currentWeapon: string = 'wave_cannon'; // Default weapon
  private defaultWeapon: string = 'wave_cannon';
  
  // Shooting
  private shootTimer: number = 0;
  private canShoot: boolean = true;
  
  // Sprite
  private sprite: HTMLImageElement;
  private spriteLoaded: boolean = false;
  private facingRight: boolean = true; // Track player direction

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.width = SETTINGS.PLAYER_SIZE * 0.7;  // Narrower for proper aspect ratio
    this.height = SETTINGS.PLAYER_SIZE;
    this.speed = SETTINGS.PLAYER_SPEED;
    this.hp = SETTINGS.PLAYER_HP;
    this.maxHp = SETTINGS.PLAYER_HP;
    this.lives = SETTINGS.PLAYER_LIVES;
    this.fireRate = SETTINGS.PLAYER_FIRE_RATE;
    
    // Load Kid Kelly sprite
    this.sprite = new Image();
    this.sprite.src = 'assets/80s-icons/kid-kelly-logo.png';
    this.sprite.onload = () => {
      this.spriteLoaded = true;
    };
  }

  public applyPowerUp(type: string, duration: number): void {
    switch(type) {
      case 'neon_shield':
        this.shieldMS = duration;
        this.shieldHits = 3;
        break;
      case 'rapid_shot':
        this.rapidMS = duration;
        break;
      case 'extra_life':
        this.lives++;
        break;
      case 'score_mult':
        this.scoreMS = duration;
        break;
      case 'bass_boost':
        this.bassMS = duration;
        break;
      case 'tempo_boost':
        this.tempoMS = duration;
        break;
      case 'double_chain':
        this.doubleMS = duration;
        break;
      case 'triple_shot':
        this.tripleMS = duration;
        break;
      case 'starburst':
        // Handled in PlayState (clear all enemies)
        break;
      case 'time_freeze':
        this.freezeMS = duration;
        break;
      case 'auto_collect':
        this.magnetMS = duration;
        break;
      // Weapon power-ups
      case 'wave_signal':
        this.weaponMS = duration;
        this.currentWeapon = 'wave_signal';
        break;
      case 'dual_cannon':
        this.weaponMS = duration;
        this.currentWeapon = 'dual_cannon';
        break;
      case 'machine_gun':
        this.weaponMS = duration;
        this.currentWeapon = 'machine_gun';
        break;
      case 'shotgun_blast':
        this.weaponMS = duration;
        this.currentWeapon = 'shotgun_blast';
        break;
      case 'fm_super_blast':
        this.weaponMS = duration;
        this.currentWeapon = 'fm_super_blast';
        break;
    }
  }

  public update(deltaTime: number): void {
    // Update invulnerability timer
    if (this.invulnerable) {
      this.invulnTimer -= deltaTime;
      if (this.invulnTimer <= 0) {
        this.invulnerable = false;
      }
    }

    // Update shoot timer
    if (!this.canShoot) {
      this.shootTimer -= deltaTime;
      if (this.shootTimer <= 0) {
        this.canShoot = true;
      }
    }
    
    // Update power-up timers
    const dt = deltaTime * 1000; // convert to milliseconds
    if (this.shieldMS > 0) {
      this.shieldMS -= dt;
      if (this.shieldMS <= 0) this.shieldHits = 0;
    }
    if (this.rapidMS > 0) this.rapidMS -= dt;
    if (this.scoreMS > 0) this.scoreMS -= dt;
    if (this.bassMS > 0) this.bassMS -= dt;
    if (this.tempoMS > 0) this.tempoMS -= dt;
    if (this.doubleMS > 0) this.doubleMS -= dt;
    if (this.tripleMS > 0) this.tripleMS -= dt;
    if (this.freezeMS > 0) this.freezeMS -= dt;
    if (this.magnetMS > 0) this.magnetMS -= dt;
    if (this.weaponMS > 0) {
      this.weaponMS -= dt;
      if (this.weaponMS <= 0) {
        // Revert to default weapon when timer expires
        this.currentWeapon = this.defaultWeapon;
      }
    }
  }

  public move(dx: number, canvasWidth: number, canvasHeight: number): void {
    // Update facing direction based on movement
    if (dx < 0) {
      this.facingRight = false;
    } else if (dx > 0) {
      this.facingRight = true;
    }
    
    // Apply tempo boost speed multiplier
    const speedMultiplier = this.tempoMS > 0 ? 1.5 : 1.0;
    this.x += dx * this.speed * speedMultiplier;
    
    // Keep player within horizontal bounds
    if (this.x < this.width / 2) {
      this.x = this.width / 2;
    }
    if (this.x > canvasWidth - this.width / 2) {
      this.x = canvasWidth - this.width / 2;
    }
    
    // Keep player in upper 75% of screen (cannot enter bottom 25% chain zone)
    const maxY = canvasHeight * 0.75 - this.height / 2;
    if (this.y > maxY) {
      this.y = maxY;
    }
  }

  public shoot(): boolean {
    if (this.canShoot) {
      this.canShoot = false;
      // Apply rapid fire multiplier (faster shooting when active)
      const fireRateMultiplier = this.rapidMS > 0 ? 0.6 : 1.0;
      this.shootTimer = 1 / (this.fireRate * fireRateMultiplier);
      return true;
    }
    return false;
  }

  public takeDamage(amount: number = 1): boolean {
    if (this.invulnerable) return false;
    
    // Check shield first
    if (this.shieldHits > 0) {
      this.shieldHits--;
      return false;
    }

    this.hp -= amount;
    
    if (this.hp <= 0) {
      this.lives--;
      if (this.lives > 0) {
        // Respawn
        this.hp = this.maxHp;
        this.invulnerable = true;
        this.invulnTimer = SETTINGS.PLAYER_INVULN_TIME;
        return false;
      } else {
        // Game over
        this.active = false;
        return true;
      }
    }

    // Set invulnerability
    this.invulnerable = true;
    this.invulnTimer = SETTINGS.PLAYER_INVULN_TIME;
    return false;
  }

  public heal(amount: number = 1): void {
    this.hp = Math.min(this.hp + amount, this.maxHp);
  }

  public addLife(): void {
    this.lives++;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Flash when invulnerable
    if (this.invulnerable && Math.floor(this.invulnTimer * 10) % 2 === 0) {
      return;
    }

    ctx.save();
    
    // Shield effect
    if (this.shieldHits > 0) {
      ctx.strokeStyle = COLORS.PRIMARY;
      ctx.lineWidth = 3;
      ctx.shadowColor = COLORS.PRIMARY;
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.width / 2 + 8, 0, Math.PI * 2);
      ctx.stroke();
    }
    
    // Render Kid Kelly sprite if loaded, otherwise fallback to triangle
    if (this.spriteLoaded && this.sprite.complete) {
      // Flip sprite based on direction
      if (!this.facingRight) {
        ctx.scale(-1, 1);
        ctx.drawImage(
          this.sprite,
          -this.x - this.width / 2,
          this.y - this.height / 2,
          this.width,
          this.height
        );
      } else {
        ctx.drawImage(
          this.sprite,
          this.x - this.width / 2,
          this.y - this.height / 2,
          this.width,
          this.height
        );
      }
    } else {
      // Fallback triangle
      ctx.translate(this.x, this.y);
      
      // Flip triangle based on direction
      if (!this.facingRight) {
        ctx.scale(-1, 1);
      }
      
      ctx.fillStyle = COLORS.PRIMARY;
      ctx.beginPath();
      ctx.moveTo(0, -this.height / 2);
      ctx.lineTo(-this.width / 2, this.height / 2);
      ctx.lineTo(this.width / 2, this.height / 2);
      ctx.closePath();
      ctx.fill();
    }
    
    ctx.restore();
  }

  public getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - this.width / 2,
      y: this.y - this.height / 2,
      width: this.width,
      height: this.height,
    };
  }

  // Getters and setters for power-ups
  public getHp(): number { return this.hp; }
  public getCurrentWeapon(): string { return this.currentWeapon; }
  public hasRapidFire(): boolean { return this.rapidMS > 0; }
  public getMaxHp(): number { return this.maxHp; }
  public getLives(): number { return this.lives; }
  public isInvulnerable(): boolean { return this.invulnerable; }
  public hasShield(): boolean { return this.shieldHits > 0; }
  public getScoreMultiplier(): number { return this.scoreMultiplier; }
  public hasAutoCollect(): boolean { return this.autoCollect; }
  public hasWideShot(): boolean { return this.hasWideBeam; }
  public hasPiercingShot(): boolean { return this.hasPiercing; }

  public setShield(hits: number): void { this.shieldHits = hits; }
  public setFireRate(rate: number): void { this.fireRate = rate; }
  public setScoreMultiplier(mult: number): void { this.scoreMultiplier = mult; }
  public setAutoCollect(enabled: boolean): void { this.autoCollect = enabled; }
  public setWideBeam(enabled: boolean): void { this.hasWideBeam = enabled; }
  public setPiercing(enabled: boolean): void { this.hasPiercing = enabled; }
  
  public getActivePowerUps(): Map<string, number> {
    const activePowerUps = new Map<string, number>();
    
    if (this.shieldMS > 0) activePowerUps.set('neon_shield', this.shieldMS);
    if (this.rapidMS > 0) activePowerUps.set('rapid_shot', this.rapidMS);
    if (this.scoreMS > 0) activePowerUps.set('score_mult', this.scoreMS);
    if (this.bassMS > 0) activePowerUps.set('bass_boost', this.bassMS);
    if (this.tempoMS > 0) activePowerUps.set('tempo_boost', this.tempoMS);
    if (this.doubleMS > 0) activePowerUps.set('double_chain', this.doubleMS);
    if (this.tripleMS > 0) activePowerUps.set('triple_shot', this.tripleMS);
    if (this.freezeMS > 0) activePowerUps.set('time_freeze', this.freezeMS);
    if (this.magnetMS > 0) activePowerUps.set('auto_collect', this.magnetMS);
    
    return activePowerUps;
  }
}
